import { useContentStore } from '@/stores/content';
import { useLoginStore } from '@/stores/login';
import axios, { AxiosError, type AxiosResponse } from 'axios';
import { updateToken } from '../authentication';
import { sleep } from '../utils';
import { LocationApi, MeetingApi, MeetingPhotoApi, MemberApi, RoomApi, ServiceApi } from './api';
import { CrewApi, MeetingPhotoApi as CrewMeetingPhotoApi } from './crew/api';
import {
  CAPEDITADMINApi,
  CAPEDITLOCATIONApi,
  CAPEDITPRIMARYApi,
  CAPEDITSECONDARYApi,
  CAPEDITSERVICEApi,
  CAPVIEWHISTORYApi,
  NOCAPApi
} from './maintenance/api';
import { BASE_PATH } from './base';
import { BASE_PATH as BASE_PATH_CREW } from './crew/base';
import { BASE_PATH as BASE_PATH_MAINTE } from './maintenance/base';
import { Configuration } from './configuration';
import { Configuration as CrewConfigration } from './crew/configuration';
import { Configuration as MainteConfigration } from './maintenance/configuration';

const config = new Configuration({
  basePath: BASE_PATH
});

const crewConfig = new CrewConfigration({
  basePath: BASE_PATH_CREW
});

const mainteConfig = new MainteConfigration({
  basePath: BASE_PATH_MAINTE
});

const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json'
  }
});

axiosInstance.interceptors.request.use(async (request) => {
  request.headers.setAuthorization(useLoginStore().token);
  return request;
});

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    switch (error.response?.status) {
      /** 401/Unauthorized */
      case 401:
        // cognitoのIDトークン期限切れ
        // IDトークンを更新する
        await updateToken().catch((err) => {
          // 更新トークン期限切れ
          useLoginStore().setTokenExpired(true);
          return Promise.reject();
        });

        // リトライ処理
        await sleep(3000);
        console.debug('リトライ処理');
        if (error.config) {
          return axiosInstance.request(error.config);
        } else {
          return Promise.reject();
        }
      default:
        return Promise.reject(error);
    }
  }
);

/** サービス関連API */
export const serviceApi = new ServiceApi(config, '', axiosInstance);

/** 会議室関連API */
export const roomApi = new RoomApi(config, '', axiosInstance);

/** 会議・案件関連API */
export const meetingApi = new MeetingApi(config, '', axiosInstance);

/** 会議中画像連携関連API */
export const meetingPhotoApi = new MeetingPhotoApi(config, '', axiosInstance);

/** ユーザ関連API */
export const memberApi = new MemberApi(config, '', axiosInstance);

/** 契約先関連API */
export const locationApi = new LocationApi(config, '', axiosInstance);

/** 隊員用API */
export const crewApi = new CrewApi(crewConfig, '', axiosInstance);

/** 隊員用API（写真撮影） */
export const crewMeetingPhotoApi = new CrewMeetingPhotoApi(crewConfig, '', axiosInstance);

/** メンテナンス系 - サービス管理API */
export const capEditServiceApi = new CAPEDITSERVICEApi(mainteConfig, '', axiosInstance);

/** メンテナンス系 - 一次専門組織管理API */
export const capEditPrimaryApi = new CAPEDITPRIMARYApi(mainteConfig, '', axiosInstance);

/** メンテナンス系 - 二次専門組織管理API */
export const capEditSecondaryApi = new CAPEDITSECONDARYApi(mainteConfig, '', axiosInstance);

/** メンテナンス系 - 対象先管理API */
export const capEditLocationApi = new CAPEDITLOCATIONApi(mainteConfig, '', axiosInstance);

/** メンテナンス系 - 管理ユーザ管理API */
export const capEditAdminApi = new CAPEDITADMINApi(mainteConfig, '', axiosInstance);

/** メンテナンス系 - 履歴閲覧API */
export const capViewHistoryApi = new CAPVIEWHISTORYApi(mainteConfig, '', axiosInstance);

/** メンテナンス系 - 管理権限不要API */
export const noCapApi = new NOCAPApi(mainteConfig, '', axiosInstance);

/**
 * APIのwrapper関数
 * @param api 実行するAPI関数
 * @param progress プログレス表示の有無
 * @returns
 */
export function execute(
  api: (...args: any) => Promise<AxiosResponse<any, any>>,
  progress: boolean = false
): Promise<any> {
  const contentStore = useContentStore();
  return new Promise(async (resolve, reject) => {
    try {
      if (progress) {
        contentStore.setProgress(true);
      }
      const response = await api();
      if (response.status === 200) {
        resolve(response.data);
      } else {
        reject();
      }
    } catch (error: unknown) {
      // エラー処理
      // if (error instanceof AxiosError) {
      //   switch () {
      //     case 400:
      //       error.response.data
      //       break;
      //     case 403:
      //       break;
      //     case 500:
      //       break;
      //     default:
      //       break;
      //   }
      // }
      if (error instanceof AxiosError) {
        console.error(error);
      }
      reject();
    } finally {
      if (progress) {
        contentStore.setProgress(false);
      }
    }
  });
}

// TODO ログイン関連の処理
// export class loginUtil {
//   loginMeeting() {
//     // return execute(() => memberApi.login()).then(...)
//   }

//   logoutMeeting() {}

//   loginAdmin() {}

//   logoutAdmin() {}
// }
